import { useMemo, type ReactNode } from 'react';
import { Badge, Button, Empty, IconButton, PrimitiveButton } from '@viquard/doc-ui/components';
import { IcnPlus } from '@viquard/doc-ui/icons';
import type {
  CalendarEvent,
  CalendarEventLink,
  CalendarGroup,
  CalendarKindMap,
} from '../types.js';
import {
  addMonths,
  buildMonthGrid,
  formatMonth,
  isSameDay,
  isoDate,
  localDateKey,
  parseISODate,
  todayPlain,
  weekdayHeaders,
} from '../utils/dateUtils.js';
import { resolveKind } from '../utils/kindTheme.js';
import { CalendarHeader } from '../components/CalendarHeader.js';
import { EventCard } from '../components/EventCard.js';
import { useSwipe } from '../hooks/useSwipe.js';
import type { EventMenuActions } from '../utils/eventMenu.js';
import { eventInstanceKey } from '../utils/identity.js';
import { formatEventCount, formatMessage, useCalendarMessages } from '../i18n.js';

export interface MonthViewMobileProps {
  readonly cursor: string;
  readonly onCursorChange: (next: string) => void;
  readonly selectedDate: string;
  readonly onSelectedDateChange: (next: string) => void;
  readonly today: string;
  readonly events: readonly CalendarEvent[];
  readonly kindTheme: CalendarKindMap;
  readonly groups?: readonly CalendarGroup[] | undefined;
  readonly locale?: string | undefined;
  readonly weekStartsOn?: 0 | 1 | 2 | 3 | 4 | 5 | 6 | undefined;
  readonly viewerTz?: string | undefined;
  readonly onSelectEvent?: ((event: CalendarEvent) => void) | undefined;
  readonly onSelectLink?:
    | ((link: CalendarEventLink, event: CalendarEvent) => void)
    | undefined;
  readonly renderEventExtras?: ((event: CalendarEvent) => ReactNode) | undefined;
  /** Slot for consumer-injected domain content keyed by the selected
   *  day (e.g. AvailabilityForDate booking cards). Renders below the
   *  day-detail card so it sits in the natural reading column on
   *  phones — desktop renders the same content in the side rail. */
  readonly renderDayRailExtras?: ((dateKey: string) => ReactNode) | undefined;
  readonly contextMenu?: ((event: CalendarEvent) => EventMenuActions) | undefined;
  readonly selectedEventKey?: string | null | undefined;
  /** When provided, the day-detail header renders a "+" affordance and
   *  the empty state offers a creation CTA — both seed the composer
   *  with the currently-selected day. The FAB exists too, but the
   *  FAB is far from the list the user is reading; an in-line entry
   *  is the natural touch target once the user is already focused on
   *  a specific day. */
  readonly onCreateEvent?:
    | ((draft?: { readonly startsAt: string }) => void)
    | undefined;
}

const MAX_DOTS = 4;

/** Phone-shaped month view: each cell is a date number plus up to 4
 *  colored dots (one per event). Below the grid, the selected day's
 *  events render as full-width cards.
 *
 *  This is the iOS / Google Calendar pattern. Picking events from inside
 *  cramped cells is replaced by a two-step "tap a day → see its list"
 *  flow that's far more usable on a touch screen. */
export function MonthViewMobile(props: MonthViewMobileProps): ReactNode {
  const messages = useCalendarMessages();
  const {
    cursor,
    onCursorChange,
    selectedDate,
    onSelectedDateChange,
    today,
    events,
    kindTheme,
    groups,
    locale,
    weekStartsOn = 0,
    viewerTz,
    onSelectEvent,
    onSelectLink,
    renderDayRailExtras,
    renderEventExtras,
    contextMenu,
    selectedEventKey,
    onCreateEvent,
  } = props;

  const cursorDate = useMemo(() => parseISODate(cursor), [cursor]);
  const todayDate = useMemo(() => parseISODate(today), [today]);
  const grid = useMemo(() => buildMonthGrid(cursorDate, weekStartsOn), [cursorDate, weekStartsOn]);
  const headers = useMemo(() => weekdayHeaders(weekStartsOn, locale, 'narrow'), [weekStartsOn, locale]);

  const eventsByDate = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>();
    events.forEach((e) => {
      const key = localDateKey(e.startsAt, viewerTz);
      const list = map.get(key);
      if (list === undefined) map.set(key, [e]);
      else list.push(e);
    });
    map.forEach((list) => list.sort((a, b) => a.startsAt.localeCompare(b.startsAt)));
    return map;
  }, [events, viewerTz]);

  const selectedEvents = eventsByDate.get(selectedDate) ?? [];
  const selectedDateObj = parseISODate(selectedDate);

  const goPrev = (): void => { onCursorChange(isoDate(addMonths(cursorDate, -1))); };
  const goNext = (): void => { onCursorChange(isoDate(addMonths(cursorDate, 1))); };

  const swipe = useSwipe({ onLeft: goNext, onRight: goPrev });

  return (
    <div className="vq-cal__split--mobile">
      <div className="vq-cal__panel vq-cal__panel--grid">
        <CalendarHeader
          label={formatMonth(cursorDate, locale)}
          onPrev={goPrev}
          onNext={goNext}
          onToday={() => {
            const t = todayPlain(viewerTz);
            onCursorChange(isoDate(t));
            onSelectedDateChange(isoDate(t));
          }}
        />
        <div className="vq-cal__weekdays" role="row">
          {headers.map((label, i) => (
            <div key={i} className="vq-cal__weekday" role="columnheader">
              <span>{label}</span>
            </div>
          ))}
        </div>
        <div
          className="vq-cal__dot-grid"
          role="grid"
          {...swipe}
        >
          {grid.map((cell, i) => {
            const key = isoDate(cell.date);
            const cellEvents = eventsByDate.get(key) ?? [];
            const isToday = isSameDay(cell.date, todayDate);
            const isSelected = key === selectedDate;
            const dotColors = cellEvents
              .slice(0, MAX_DOTS)
              .map((e) => resolveKind(kindTheme, e.kind).color);
            const overflow = cellEvents.length - dotColors.length;
            const countLabel = formatEventCount(messages, cellEvents.length);

            return (
              <PrimitiveButton
                key={key + i}
                role="gridcell"
                aria-selected={isSelected}
                aria-current={isToday ? 'date' : undefined}
                aria-label={formatMessage(messages.monthCellAriaLabel, {
                  date: key,
                  countLabel,
                })}
                disabled={cell.outOfMonth}
                onClick={() => { onSelectedDateChange(key); }}
                className={[
                  'vq-cal__dot-cell',
                  cell.outOfMonth ? 'is-out' : '',
                  isToday ? 'is-today' : '',
                  isSelected ? 'is-selected' : '',
                ].filter(Boolean).join(' ')}
              >
                <span className="vq-cal__dot-cell-date">{cell.date.day}</span>
                <span className="vq-cal__dot-cell-dots" aria-hidden="true">
                  {dotColors.map((color, j) => (
                    <span
                      key={j}
                      className="vq-cal__dot"
                      style={{ background: color }}
                    />
                  ))}
                  {overflow > 0 && (
                    <span className="vq-cal__dot-overflow">+{overflow}</span>
                  )}
                </span>
              </PrimitiveButton>
            );
          })}
        </div>
      </div>

      <section className="vq-cal__day-detail" aria-label={messages.dayDetailAriaLabel}>
        <header className="vq-cal__day-detail-head">
          <div className="vq-cal__day-detail-headline">
            <div className="vq-cal__day-detail-text">
              <div className="vq-cal__day-detail-eyebrow">
                {new Date(
                  selectedDateObj.year,
                  selectedDateObj.month - 1,
                  selectedDateObj.day,
                ).toLocaleDateString(locale, { weekday: 'long' })}
              </div>
              <h3 className="vq-cal__day-detail-title">
                {new Date(
                  selectedDateObj.year,
                  selectedDateObj.month - 1,
                  selectedDateObj.day,
                ).toLocaleDateString(locale, { month: 'long', day: 'numeric' })}
              </h3>
            </div>
            {onCreateEvent !== undefined && (
              <IconButton
                label={messages.addEventOnDay}
                variant="ghost"
                size="sm"
                onClick={() => { onCreateEvent({ startsAt: selectedDate }); }}
              >
                <IcnPlus size={16} />
              </IconButton>
            )}
          </div>
          <Badge
            tone="neutral"
            size="md"
            numeric
            label={formatMessage(messages.dayEventsBadgeLabel, {
              countLabel: formatEventCount(messages, selectedEvents.length),
            })}
          >
            {selectedEvents.length}
          </Badge>
        </header>
        {selectedEvents.length === 0 ? (
          <Empty
            size="compact"
            title={messages.nothingScheduled}
            description={
              onCreateEvent !== undefined
                ? messages.addFirstOfDay
                : messages.pickAnotherDay
            }
            {...(onCreateEvent !== undefined
              ? {
                  action: (
                    <Button
                      variant="secondary"
                      size="sm"
                      iconLeft={<IcnPlus size={14} />}
                      onClick={() => { onCreateEvent({ startsAt: selectedDate }); }}
                    >
                      {messages.newEvent}
                    </Button>
                  ),
                }
              : {})}
          />
        ) : (
          <div className="vq-cal__card-stack">
            {selectedEvents.map((e) => (
              <EventCard
                key={eventInstanceKey(e)}
                event={e}
                kindTheme={kindTheme}
                groups={groups}
                onClick={onSelectEvent}
                onSelectLink={onSelectLink}
                viewerTz={viewerTz}
                extras={renderEventExtras?.(e)}
                selected={selectedEventKey === eventInstanceKey(e)}
                contextMenu={contextMenu?.(e)}
              />
            ))}
          </div>
        )}
      </section>
      {renderDayRailExtras?.(selectedDate)}
    </div>
  );
}
